<script lang="ts">
import ProjectCards from '@/components/ProjectCards.vue'
import PageContainer from '@/components/PageContainer.vue'
import ProjectVideoPlayer from '@/components/ProjectVideoPlayer.vue'
import ImageGallery from '@/components/ImageGallery.vue'
import { ContactButton } from '@/components/button'
import { api } from '@/api/api.js'
import { updateMeta, addJsonLd, getVideoSchema, getCanonical } from '@/utils/seo.js'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import type { Author, Project } from '@/types/api'

function normalizeHref(rawHref: string | null | undefined, linkText: string): string {
  const href = (rawHref || '').trim()
  const text = (linkText || '').trim()

  if (href === 'https://' || href === 'http://' || href === '') {
    if (/^https?:\/\//i.test(text)) return text
    if (text && !text.includes(' ') && text.includes('.')) return `https://${text}`
    return '#'
  }

  if (href.startsWith('//')) return `https:${href}`
  if (href.startsWith('#')) return href
  if (/^https?:\/\//i.test(href)) return href

  if (!href.includes(' ') && href.includes('.')) return `https://${href}`

  return href
}

function postProcessLinks(html: string): string {
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') return html
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const anchors = Array.from(doc.querySelectorAll('a[href]'))
    for (const a of anchors) {
      const href = a.getAttribute('href')
      const text = a.textContent || ''
      const normalized = normalizeHref(href, text)
      a.setAttribute('href', normalized)
      a.setAttribute('target', '_blank')
      a.setAttribute('rel', 'noopener noreferrer')
    }
    return doc.body.innerHTML
  } catch {
    return html
  }
}

function normalizeMarkdownBold(text: string): string {
  let out = text.replace(/\n\s*\*\*/g, '**\n**').replace(/\s+\*\*/g, '**')
  const count = (out.match(/\*\*/g) || []).length
  if (count % 2 !== 0) out += '**'
  return out
}

function markdownLines(text: string): string {
  const lines = text
    .split(/\n/)
    .map((s) => s.trim())
    .filter(Boolean)
  if (lines.length === 0) return ''
  let html = lines
    .map((line) => {
      const parsed = marked.parse(normalizeMarkdownBold(line), { async: false }) as string
      if (/^\[[^\]]+\]\([^)]+\)\s*$/.test(line)) {
        return parsed.replace('<p>', '<p class="author-long-description__link">')
      }
      return parsed
    })
    .join('')

  html = DOMPurify.sanitize(html, {
    ADD_ATTR: ['target', 'rel'],
  })
  return postProcessLinks(html)
}

export default {
  components: {
    ProjectCards,
    PageContainer,
    ProjectVideoPlayer,
    ImageGallery,
    ContactButton,
  },
  data() {
    return {
      author: {} as Author,
      name: (this as any).$route.params.name as string,
      authorProjects: [] as Project[],
      isLoading: true as boolean,
      error: null as string | null,
      showreelAutoPlayed: false as boolean,
      /** На узком экране шоурил ведёт себя как плеер на странице проекта (контролы, без автозапуска по hover). */
      isMobileLayout: false as boolean,
    }
  },
  watch: {
    author(a: Author) {
      if (!a?.title) return
      updateMeta({
        title: `Автор — ${a.title}`,
        description: (
          a.description ||
          a.long_description ||
          `Профиль автора ${a.title} в Chapterfour.`
        ).slice(0, 160),
        canonicalPath: this.$route.path,
        ogImage: a.photo_url || a.showreel_cover_url || undefined,
      })
      if (a.showreel_url) {
        addJsonLd(
          getVideoSchema({
            name: `Шоурил — ${a.title}`,
            description: (a.description || '').slice(0, 200),
            thumbnailUrl: a.showreel_cover_url || a.photo_url,
            contentUrl: a.showreel_url.endsWith?.('.mp4') ? a.showreel_url : undefined,
            embedUrl: getCanonical(this.$route.path),
          }),
          'seo-json-ld-extra',
        )
      }
    },
  },
  computed: {
    renderedDescription(): string {
      const raw = this.author?.description || ''
      if (!raw.trim()) return ''
      try {
        return markdownLines(raw)
      } catch {
        return DOMPurify.sanitize(raw.replace(/\*\*/g, '').trim())
      }
    },
    renderedLongDescription(): string {
      const a = this.author as Record<string, unknown>
      const raw =
        this.author?.long_description ??
        a?.long_description ??
        a?.bio ??
        a?.content ??
        a?.about ??
        ''
      const str = typeof raw === 'string' ? raw : ''
      if (!str.trim()) return ''
      try {
        return markdownLines(str)
      } catch {
        return DOMPurify.sanitize(str.replace(/\*\*/g, '').trim())
      }
    },
    showreelVideoSources(): { src: string; type: string }[] {
      if (!this.author?.showreel_url) return []
      return [
        {
          src: this.author.showreel_url,
          type: this.getVideoType(this.author.showreel_url),
        },
      ]
    },
    showreelCoverImage(): string | null {
      // Проверяем различные возможные поля для обложки showreel
      return (
        this.author?.showreel_cover_url ||
        this.author?.showreel_cover_image ||
        this.author?.cover_url ||
        this.author?.cover_image ||
        null
      )
    },
    telegramValue(): string {
      // Проверяем оба возможных поля для Telegram
      const v = this.author?.telegram_username || this.author?.telegram_channel

      const trimmed = typeof v === 'string' ? v.trim() : ''
      return trimmed && trimmed !== 'None' ? trimmed : ''
    },
    emailValue(): string {
      const v = this.author?.email

      const trimmed = typeof v === 'string' ? v.trim() : ''
      return trimmed && trimmed !== 'None' ? trimmed : ''
    },
    phoneValue(): string {
      const v = this.author?.phone || this.author?.phone_number

      const trimmed = typeof v === 'string' ? v.trim() : ''
      return trimmed && trimmed !== 'None' ? trimmed : ''
    },
    hasContacts(): boolean {
      return Boolean(this.telegramValue || this.emailValue || this.phoneValue)
    },
    authorGalleryImages(): { url: string }[] {
      // Галерея изображений приходит в массиве backstage_images
      const images = this.author?.backstage_images || []

      // Если это массив строк, преобразуем в массив объектов
      if (Array.isArray(images) && images.length > 0) {
        return images.map((img) => {
          if (typeof img === 'string') {
            return { url: img }
          }
          return img
        })
      }

      return []
    },
    hasGalleryImages(): boolean {
      return this.authorGalleryImages.length > 0
    },
    commercialFilled(): boolean {
      return this.author?.commercial_filled === true
    },
  },
  methods: {
    checkAuthorMobileLayout() {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
      this.isMobileLayout = window.matchMedia('(max-width: 48rem)').matches
    },
    autoPlayShowreel() {
      if (this.isMobileLayout) return
      if (this.showreelAutoPlayed) return
      const player = (this.$refs as any)?.authorShowreelPlayer
      if (player && typeof player.handleVideoMouseEnter === 'function') {
        this.showreelAutoPlayed = true
        try {
          player.handleVideoMouseEnter()
        } catch (e) {
          console.error('Не удалось автозапустить шоурил автора:', e)
        }
      }
    },
    getVideoType(url: string | undefined): string {
      if (!url) return 'video/mp4'
      if (url.endsWith('.m3u8')) return 'application/x-mpegURL'
      if (url.endsWith('.webm')) return 'video/webm'
      if (url.endsWith('.ogg')) return 'video/ogg'
      return 'video/mp4'
    },
    getTelegramHref(): string {
      const handle = this.telegramValue
      if (!handle) return '#'
      // если пришла чистая собака/ник — собираем полноценную ссылку
      if (handle.startsWith('@')) {
        return `https://t.me/${handle.slice(1)}`
      }
      if (!handle.startsWith('http')) {
        return `https://t.me/${handle}`
      }
      return handle
    },
    getEmailHref(): string {
      const email = this.emailValue
      if (!email) return '#'
      return email.startsWith('mailto:') ? email : `mailto:${email}`
    },
    getPhoneHref(): string {
      const phone = this.phoneValue
      if (!phone) return '#'
      const cleanPhone = phone.replace(/\D/g, '')
      return cleanPhone ? `tel:${cleanPhone}` : '#'
    },
  },
  mounted() {
    this.checkAuthorMobileLayout()
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.checkAuthorMobileLayout)
    }
  },
  beforeUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.checkAuthorMobileLayout)
    }
  },
  async created() {
    if (typeof window !== 'undefined') {
      this.checkAuthorMobileLayout()
    }
    this.isLoading = true
    this.error = null
    try {
      this.author = await api.getAuthor(this.name)
      if (this.author.photo_url) {
        this.author.photo_url = encodeURI(this.author.photo_url.replace(/\u202f/g, ' '))
      }

      this.authorProjects = await api.getAuthorProjects(this.name)
      if (this.author?.showreel_url) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.autoPlayShowreel()
          }, 300)
        })
      }
    } catch (err) {
      console.error('Ошибка при получении данных автора:', err)
      this.error = 'Не удалось загрузить данные автора. Попробуйте позже.'
    } finally {
      this.isLoading = false
    }
  },
}
</script>

<template>
  <PageContainer>
    <div v-if="isLoading" class="author-loader">
      <div class="author-loader-content">
        <div class="author-loader-header"></div>
        <div class="author-loader-subtitle"></div>
        <div class="author-loader-line"></div>
        <div class="author-loader-line author-loader-line--short"></div>
        <div class="author-loader-line author-loader-line--short"></div>
        <div class="author-loader-contacts">
          <div class="author-loader-contact"></div>
          <div class="author-loader-contact"></div>
          <div class="author-loader-contact"></div>
        </div>
      </div>
      <div class="author-loader-image"></div>
    </div>
    <div v-else-if="error" class="author-error">
      <p>{{ error }}</p>
    </div>
    <div v-else class="author-container">
      <header class="author-header">
        <h1 class="text-xl">{{ author.title }}</h1>
        <div
          v-if="renderedDescription"
          class="text-md author-description author-description--md"
          v-html="renderedDescription"
        />
        <p v-else class="text-md">{{ author.description }}</p>
        <div
          v-if="renderedLongDescription"
          class="text-sm author-long-description author-long-description--md"
          v-html="renderedLongDescription"
        />
        <p v-else class="text-sm author-long-description">Нет дополнительной информации</p>

        <!-- Контакты и коммерческое предложение -->
        <div class="author-contacts-block">
          <div v-if="hasContacts" class="author-contacts">
            <ContactButton
              v-if="telegramValue"
              type="telegram"
              :href="getTelegramHref()"
              :telegram="telegramValue"
            />
            <ContactButton
              v-if="emailValue"
              type="mail"
              :href="getEmailHref()"
              :email="emailValue"
            />
            <ContactButton
              v-if="phoneValue"
              type="phone"
              :href="getPhoneHref()"
              :phone="phoneValue"
            />
          </div>
          <router-link
            v-if="commercialFilled"
            :to="`/author/${name}/kp`"
            class="author-commercial-link"
          >
            <img class="author-commercial-link__icon" src="/static/icons/document.svg" alt="" />
            <span class="author-commercial-link__text-wrapper">
              <span class="author-commercial-link__text author-commercial-link__text--default"
                >КОММЕРЧЕСКОЕ ПРЕДЛОЖЕНИЕ</span
              >
              <span class="author-commercial-link__text author-commercial-link__text--hover"
                >ПЕРЕЙТИ НА СТРАНИЦУ</span
              >
            </span>
          </router-link>
        </div>
      </header>
      <div
        class="author-image"
        v-if="author.photo_url"
        :style="{ backgroundImage: `url(${author.photo_url})` }"
        :title="author.photo_url || 'No image'"
      >
        <p v-if="!author.photo_url">URL изображения отсутствует</p>
      </div>
    </div>
    <div
      class="author-showreel"
      v-if="!isLoading && author.showreel_url && showreelVideoSources.length > 0"
    >
      <!-- <p class="text-lg">SHOWREEL</p> -->
      <ProjectVideoPlayer
        ref="authorShowreelPlayer"
        :video-sources="showreelVideoSources"
        :title="author.title || 'SHOWREEL'"
        :cover-image="showreelCoverImage"
        loader="Загрузка видео..."
        :hide-video-controls="!isMobileLayout"
        :auto-play-on-hover="!isMobileLayout"
      />
    </div>
    <div class="author-projects" v-if="!isLoading && authorProjects.length > 0">
      <p class="text-lg text-left w-100 projects-title">ПРОЕКТЫ АВТОРА</p>
      <ProjectCards :projects="authorProjects" />
    </div>
    <div v-if="!isLoading && hasGalleryImages" class="author-gallery">
      <ImageGallery :images="authorGalleryImages" />
    </div>
  </PageContainer>
</template>

<style scoped>
.author-loader {
  margin: 5rem 0 2.5rem;
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 2rem;
  align-items: center;
}

.author-loader-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.author-loader-header,
.author-loader-subtitle,
.author-loader-line {
  height: 1.25rem;
  border-radius: 0;
  background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
  background-size: 200% 100%;
  animation: author-skeleton-shimmer 1.4s ease-in-out infinite;
}

.author-loader-header {
  width: 50%;
}

.author-loader-subtitle {
  width: 35%;
}

.author-loader-line {
  width: 100%;
  margin-top: 0.25rem;
}

.author-loader-line--short {
  width: 90%;
}

.author-loader-contacts {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 2rem;
}

.author-loader-contact {
  width: 8rem;
  height: 2rem;
  border-radius: 0;
  background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
  background-size: 200% 100%;
  animation: author-skeleton-shimmer 1.4s ease-in-out infinite;
}

.author-loader-image {
  width: 80%;
  aspect-ratio: 1 / 1;
  margin-left: auto;
  border-radius: 0;
  background: linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%);
  background-size: 200% 100%;
  animation: author-skeleton-shimmer 1.4s ease-in-out infinite;
}

.author-error {
  margin: 5rem 0 2.5rem;
  text-align: center;
  color: #b91c1c;
}

@keyframes author-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.author-container {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 2rem;
  align-items: center;
  margin: 3rem 0 1.5rem;
}

.author-header {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 0.5rem;

  p {
    margin-bottom: 0.25rem;
  }
}

.author-long-description {
  text-transform: none;
  margin-top: 0; /* reset; set only for the first paragraph below */
  font-size: 1rem;
}

.author-description--md :deep(strong) {
  font-weight: 700;
}

.author-long-description--md :deep(p) {
  margin-bottom: 0.5rem;
}

.author-long-description--md :deep(p:last-child) {
  margin-bottom: 0;
}

.author-long-description--md :deep(ul),
.author-long-description--md :deep(ol) {
  margin: 0.5rem 0 0.5rem 1.25rem;
  padding: 0;
}

.author-long-description--md :deep(li) {
  margin-bottom: 0.25rem;
}

.author-long-description--md :deep(strong) {
  font-weight: 700;
}

.author-long-description--md :deep(a) {
  color: inherit;
  text-decoration: underline;
}

.author-long-description--md :deep(.author-long-description__link) {
  margin-top: 1rem;
}

.author-long-description--md :deep(h1),
.author-long-description--md :deep(h2),
.author-long-description--md :deep(h3) {
  font-size: 1rem;
  font-weight: 600;
  margin: 0.75rem 0 0.25rem;
}

.author-contacts-block {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 1.25rem;
  align-items: flex-start;
}

.author-contacts {
  display: flex;
  flex-direction: column;
}

.author-commercial-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  flex-shrink: 0;
}

.author-commercial-link__icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.author-commercial-link__text-wrapper {
  position: relative;
  display: inline-block;
  text-align: center;
}

.author-commercial-link__text {
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: -0.01155rem;
  text-transform: uppercase;
  white-space: nowrap;
  transition: opacity 0.3s ease-in-out;
  display: inline-block;
}

.author-commercial-link__text--hover {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 0.05rem;
  opacity: 0;
  text-transform: uppercase;
  pointer-events: none;
}

.author-commercial-link:hover .author-commercial-link__text--default {
  opacity: 0;
}

.author-commercial-link:hover .author-commercial-link__text--hover {
  opacity: 1;
}

/* Apply top margin only to the first long-description paragraph (the one right after .text-md) */
.author-header .text-md + .author-long-description {
  margin-top: 1.25rem;
}

.author-image {
  flex: 1;
  width: 80%;
  aspect-ratio: 1 / 1;
  background-size: cover;
  background-position: center;
  background-color: var(--color-gray-300);
  background-repeat: no-repeat;
  margin-left: auto;
}

.author-showreel {
  margin: 3rem 0 1.5rem;
}

.author-showreel .text-lg {
  text-align: left;
}

.author-showreel .video-player {
  max-width: 100%;
}

/* Десктоп: шоурил без нижних контролов — кнопку play не показываем (запуск по hover). На мобильных плеер как на проекте — кнопка видна. */
.author-showreel .video-player__play-pause-button {
  display: none;
}

@media (max-width: 48rem) {
  .author-showreel .video-player__play-pause-button {
    display: flex;
  }
}

.author-projects {
  margin: 3rem 0 1.5rem;
}

.projects-title {
  font-weight: 400;
  user-select: none;
  margin-bottom: 0;
}

.author-gallery {
  margin: 1.25rem 0;
}

.author-gallery :deep(.image-gallery) {
  margin-top: 0;
  margin-bottom: 0;
}

@media (max-width: 48rem) {
  .author-loader {
    display: flex;
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .author-loader-image {
    width: 100%;
    margin-left: 0;
  }

  .author-container {
    display: flex;
    flex-direction: column-reverse;
    align-items: stretch;
    margin-top: 0;
    margin-bottom: 1.5rem;
  }

  .author-header {
    margin-right: 0;
    margin-top: 0;
    text-align: start;
  }

  .author-image {
    height: auto;
    aspect-ratio: 1 / 1;
    width: 100vw;
    max-width: 100vw;
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
  }

  .author-header .text-md + .author-long-description {
    margin-top: 0.75rem;
  }

  .author-contacts {
    flex-direction: column;
    align-items: flex-start;
  }

  .author-contacts-block {
    flex-direction: column-reverse;
    gap: 2rem;
  }
}

@media (max-width: 43.75rem) {
  .author-showreel {
    margin: 1.5rem 0 1rem;
  }

  .author-projects {
    margin: 1.5rem 0 1rem;
  }

  .author-gallery {
    margin: 1.25rem 0 1rem;
  }
}
</style>
